import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { Subscription } from 'rxjs';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';

import { TableColumnTypeEnum, TableFilterTypeEnum } from './shared/enums';
import { TableConfig, TableFilters } from './shared/models';

/**
 * A common table component that implements lazy loading and virtual scroll.
 * It is possible to add filters.
 * /!\ All the code below is necessary to do display properly the table. The backend must implement the Content-Range.
 *
 * @example
 * <app-filters-side-panel [allowSiteGroups]="false">
 *   <mat-tab-group mat-align-tabs="start">
 *     <mat-tab>
 *       <ng-template matTabLabel>
 *         <span translate="..."></span>
 *       </ng-template>
 *       <ng-template matTabContent>
 *         <app-search-bar
 *           [searchKey]="'...'"
 *           [primaryButtonKey]="'...'"></app-search-bar>
 *         <app-table [config]="config" [filters]="filters"></app-table>
 *       </ng-template>
 *     </mat-tab>
 *   </mat-tab-group>
 * </app-filters-side-panel>
 *
 * ...
 *
 * config: TableConfig = {
 *   displayedColumns: ['column1', ...],
 *   columns: [
 *     { name: 'recipient_id', translationKey: 'engagement/history/recipient-id', property: 'recipient_id' },
 *     ...
 *   ],
 *   actions: [{ tooltip: 'common/change', clickFn: () => console.log(666), icon: 'visibility' }],
 * };
 * filters: TableFilters = {
 *   status: { name: 'sendStatus', items: [], placeholder: 'engagement/history/filters/status' },
 *   ...
 * };
 *
 * ...
 *
 *  this.searchBarService.sort = { active: '...', direction: '...' };
 *  this.searchBarService.referer$ = service.findAll();
 *  // With service.findAll defined as:
 *  // findAll = (): Observable<HttpResponse<any[]>> => {
 *  // const body = this.searchBarService.buildCriteria();
 *  // return this.http
 *  //   .post<any[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/...`, body, {
 *  //     observe: 'response',
 *  //   })
 *  //   .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
 *  // };
 *  this.searchBarService.formatter = (element: any) => { ... };
 *  this.searchBarService.submitQuery();
 */
@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TableComponent implements OnInit, OnDestroy {
  @Input() config: TableConfig;
  @Input() filters: TableFilters;

  dataSource = new TableVirtualScrollDataSource([]);

  get columns() {
    return this.config?.columns || [];
  }

  get displayedColumns(): string[] {
    return this.config?.displayedColumns || [];
  }

  get actions() {
    return this.config?.actions || [];
  }

  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('dummyAvailableHeight') dummyAvailableHeight: ElementRef<HTMLDivElement>;

  maxHeight = 500;

  private subscriptions = new Subscription();

  TableColumnTypeEnum = TableColumnTypeEnum;
  TableFilterTypeEnum = TableFilterTypeEnum;

  /**
   * Allows to have a full responsive table by taking the available height. The overflow of the table will be visible by using a scroll.
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    const virtualScrollViewport: HTMLElement = document.querySelector('cdk-virtual-scroll-viewport');

    // 60 is the line's height.
    this.maxHeight =
      Math.min(
        this.dummyAvailableHeight.nativeElement.clientHeight - virtualScrollViewport?.offsetTop,
        60 * this.searchBarService.fetchSize
      ) || this.maxHeight;

    const headersItems: NodeListOf<HTMLElement> = document.querySelectorAll('.headers-purpose thead > tr > th');
    const cellsItems: NodeListOf<HTMLElement> = document.querySelectorAll('.content-purpose tbody tr:first-child > td');
    if (headersItems && cellsItems && headersItems[0] && cellsItems[0]) {
      this.displayedColumns.forEach(
        (key, index) => (headersItems[index].style.width = `${cellsItems[index].getBoundingClientRect().width}px`)
      );
    }
  }

  constructor(public searchBarService: SearchBarService, public dialog: MatDialog) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.searchBarService.itemsBS.subscribe(values => {
        if (values) {
          this.messagesFetched(values);
        }
      })
    );

    // search in items for "async requests" mode
    this.subscriptions.add(
      this.searchBarService.searchAndSortBS.subscribe(result => {
        if (!this.searchBarService.refererAsync$ || result === false) {
          return;
        }

        if (result === true) {
          this.dataSource.filter = '';
        } else if (typeof result === 'string') {
          this.dataSource.filter = result;
        }
      })
    );
  }

  messagesFetched(values) {
    const dataSource: any[] = values;
    this.dataSource = new TableVirtualScrollDataSource(dataSource);
    setTimeout(() => this.onResize());
  }

  sortChange(event: Sort) {
    if (this.searchBarService.refererAsync$) {
      const dataSource: any[] = this.dataSource.sortData(this.dataSource.data, this.sort);
      this.dataSource = new TableVirtualScrollDataSource(dataSource);
    } else {
      this.searchBarService.sortData(event);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
